import { useId, useState } from "react";
import {
  Anchor,
  ArrowLeftSVG,
  Button,
  ChalkboardSVG,
  Icon,
  RefreshSVG,
  type SvgComponentType,
  UserFilledSVG,
  UserGroupSVG,
} from "@newsela/angelou";
import { useRouter } from "next/router";

import {
  accountOptionAnchorCss,
  accountOptionButtonCss,
  accountOptionsContainerCss,
  accountOptionsTitleCss,
  accountOptionsUnorderedListCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/UserProfileMenu/UserProfilePopout/PopoutAccountOptions/PopoutAccountOptions.styles";
import { useSettingsDirectoryPrivileges } from "@/account/withSettingsDirectoryPrivileges/withSettingsDirectoryPrivileges";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import { ACCOUNT_OPTIONS } from "@/settings/SettingsBasePage/SettingsBasePage.constants";
import useSignOut from "@/users/v3/useSignOut";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import useUser from "@/users/useUser";

import { stripRedirectQueryParams } from "@/utils/appendTrackingParamsToUrl";

import {
  HOMEPAGE_SESSION_KEY,
  LUNA_SESSION_KEY,
  SEARCH_SESSION_KEY,
  SEARCH_SESSION_QUERY_KEY,
} from "../../../shared/constants";

interface AccountOption {
  label: string;
  href: string;
  SvgComponent: SvgComponentType;
  qaSelector: string;
  isVisible: boolean;
}

const PopoutAccountOptions = () => {
  const { push, asPath: currentPath } = useRouter();
  const { data: user } = useUser();
  const { canViewTheirLicenses, isImpersonated } = useGetUserProperties();
  const canViewDirectory = !!useSettingsDirectoryPrivileges(user);
  const [isSignOutLoading, setIsSignOutLoading] = useState(false);
  const titleId = useId();

  const { signOut } = useSignOut();

  const handleAsyncSignOut = async () => {
    sessionStorage.removeItem(HOMEPAGE_SESSION_KEY);
    sessionStorage.removeItem(LUNA_SESSION_KEY);
    sessionStorage.removeItem(SEARCH_SESSION_KEY);
    sessionStorage.removeItem(SEARCH_SESSION_QUERY_KEY);
    await signOut();
  };

  const handleSignout = () => {
    setIsSignOutLoading(true);

    if (isImpersonated) {
      void push("/impersonate-stop");
      return;
    }

    void handleAsyncSignOut()
      .catch(() => null)
      .then(() => {
        const signInUrl = `${MONOLITH_ORIGIN}/signin`;
        const cleanPath = stripRedirectQueryParams(currentPath);
        const encodedRedirectUrl = encodeURIComponent(cleanPath);
        const signInAndRedirectUrl = `${signInUrl}?next=${encodedRedirectUrl}`;
        void push(signInAndRedirectUrl);
      });
  };

  const options: AccountOption[] = [
    {
      label: ACCOUNT_OPTIONS.PROFILE.content,
      href: `${ACCOUNT_OPTIONS.PROFILE.url}/`,
      SvgComponent: UserFilledSVG,
      qaSelector: "global-nav-settings",
      isVisible: true,
    },
    {
      label: ACCOUNT_OPTIONS.CLASS.content,
      href: `${ACCOUNT_OPTIONS.CLASS.url}/`,
      SvgComponent: ChalkboardSVG,
      qaSelector: "global-nav-class-directory",
      isVisible: canViewDirectory,
    },
    {
      label: ACCOUNT_OPTIONS.STUDENTS.content,
      href: `${ACCOUNT_OPTIONS.STUDENTS.url}/`,
      SvgComponent: UserGroupSVG,
      qaSelector: "global-nav-student-directory",
      isVisible: canViewDirectory,
    },
    {
      label: ACCOUNT_OPTIONS.SUBSCRIPTIONS.content,
      href: `${ACCOUNT_OPTIONS.SUBSCRIPTIONS.url}/`,
      SvgComponent: RefreshSVG,
      qaSelector: "global-nav-subscriptions",
      isVisible: !!canViewTheirLicenses,
    },
  ];

  return (
    <div css={accountOptionsContainerCss}>
      <div id={titleId} css={accountOptionsTitleCss}>
        Manage account
      </div>

      <ul aria-labelledby={titleId} css={accountOptionsUnorderedListCss}>
        {options
          .filter(({ isVisible }) => isVisible)
          .map(({ label, href, SvgComponent, qaSelector }) => (
            <li key={href}>
              <Anchor
                dataProps={{ "data-qa-selector": qaSelector }}
                href={href}
                __cssFor={accountOptionAnchorCss}
              >
                <Icon SvgComponent={SvgComponent} />
                {label}
              </Anchor>
            </li>
          ))}
        <li>
          <Button
            onClick={handleSignout}
            __cssFor={accountOptionButtonCss}
            flavor={Button.flavors.incognito}
            dataProps={{ "data-qa-selector": "global-nav-sign-out" }}
            disabled={isSignOutLoading}
          >
            <Icon SvgComponent={ArrowLeftSVG} />
            {isImpersonated && "Stop impersonating"}
            {!isImpersonated && "Sign out"}
          </Button>
        </li>
      </ul>
    </div>
  );
};

PopoutAccountOptions.displayName = "PopoutAccountOptions";
export { PopoutAccountOptions };
